import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from 'axios';

// Hook for gradual number animation
const useAnimatedValue = (targetValue, duration = 800, enabled = true) => {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef(null);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(null);
  const targetValueRef = useRef(targetValue);
  const isFirstRender = useRef(true);

  useEffect(() => {
    targetValueRef.current = targetValue;

    // On first render or if disabled, set immediately without animation
    if (isFirstRender.current || !enabled) {
      setDisplayValue(targetValue);
      isFirstRender.current = false;
      return;
    }

    if (targetValue === displayValue) return;

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValueRef.current + (targetValueRef.current - startValueRef.current) * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValueRef.current);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, enabled]);

  return displayValue;
};

// Component for animated time display
const AnimatedTimeDisplay = ({ timeString, isLoading }) => {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  const animatedSeconds = useAnimatedValue(totalSeconds, 800, !isLoading);

  const animatedHours = Math.floor(animatedSeconds / 3600);
  const animatedMinutes = Math.floor((animatedSeconds % 3600) / 60);
  const animatedSecs = animatedSeconds % 60;

  const formatTime = (value) => {
    return value < 10 ? `0${value}` : value;
  };

  const formattedTime = `${formatTime(animatedHours)}:${formatTime(animatedMinutes)}:${formatTime(animatedSecs)}`;

  if (isLoading) {
    return (
      <span
        style={{
          width: "80px",
          height: "18px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
          display: "inline-block",
        }}
      />
    );
  }

  return <span>{formattedTime}</span>;
};

function WeekTotalDisplay({ startDate, endDate, selectedUser }) {
  const token = useSelector((state) => state.auth.token);
  const timerChange = useSelector((state) => state.timerData.timerDataState);

  const {
    data: weekTotalSeconds,
    isLoading,
  } = useQuery(
    [
      "weekTotal",
      {
        startDate,
        endDate,
        selectedUser,
        timerChange,
      },
    ],
    async () => {
      const response = await axios.post(
        "/api/time-track/week-total",
        {
          startDate,
          endDate,
          zone: global.config.getTimeZone(),
          selectedUser,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const parsed = parseInt(response.data, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    },
    {
      enabled: !!token && !!startDate && !!endDate && !!selectedUser,
      keepPreviousData: true,
      retry: 1,
    }
  );

  const isInitialLoading = isLoading && (weekTotalSeconds == null || Number.isNaN(weekTotalSeconds));

    // Format time string for AnimatedTimeDisplay
  const formatTime = (value) => {
    return value < 10 ? `0${value}` : value;
  };

  const safeSeconds =
    weekTotalSeconds != null && !Number.isNaN(weekTotalSeconds) ? weekTotalSeconds : 0;

  const timeString = `${formatTime(Math.floor(safeSeconds / 3600))}:${formatTime(
    Math.floor((safeSeconds % 3600) / 60)
  )}:${formatTime(Math.floor(safeSeconds % 60))}`;

  if (isInitialLoading) {
    return (
      <span
        style={{
          width: "80px",
          height: "18px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
          display: "inline-block",
        }}
      />
    );
  }

  return <span>{timeString}</span>;
}

export default WeekTotalDisplay;